import { useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Position a rail flyout panel next to its trigger so it ALWAYS fits the
 * viewport on any screen size. The panel is anchored to the trigger's right
 * edge and opens toward whichever side (below/above) has more room, capped to
 * that available height so it never clips off the top or bottom — the panel
 * scrolls internally instead. Returned coords are for `position: fixed`.
 */
export function computeRailPanelPos(
  rect: DOMRect,
  opts?: { gap?: number; margin?: number },
): { left: number; top?: number; bottom?: number; maxHeight: number } {
  const gap = opts?.gap ?? 8;
  const margin = opts?.margin ?? 8;
  const left = rect.right + gap;
  const spaceBelow = window.innerHeight - rect.top;
  const spaceAbove = rect.bottom;
  if (spaceBelow >= spaceAbove) {
    return { left, top: rect.top, maxHeight: Math.max(120, spaceBelow - margin) };
  }
  return { left, bottom: window.innerHeight - rect.bottom, maxHeight: Math.max(120, spaceAbove - margin) };
}

/**
 * Hover/focus tooltip for the icon rail. The label is rendered in a portal on
 * <body> with fixed positioning computed from the trigger's live viewport rect,
 * so it is never clipped by the rail's own scroll overflow (the rail scrolls
 * vertically on short screens).
 */
export function RailTooltip({ label, children }: { label: string; children: ReactNode }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const show = () => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.top + r.height / 2, left: r.right + 8 });
  };
  const hide = () => setPos(null);

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocusCapture={show}
      onBlurCapture={hide}
    >
      {children}
      {pos &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[60] -translate-y-1/2 whitespace-nowrap rounded-md bg-neutral-800 px-2.5 py-1 text-[11px] font-medium text-white shadow-lg"
            style={{ top: pos.top, left: pos.left }}
          >
            {label}
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-neutral-800" />
          </div>,
          document.body,
        )}
    </div>
  );
}
